import React from 'react';
import { MonitorTask } from '../../services/klineMonitorService';
import { BarInterval, ORDER_SIDE } from '@/services/enums';
import { getTimeFrameName } from '../../utils/timeFrameUtils';
import { getSignalStatus } from './utils';

interface MonitorCardProps {
  monitor: MonitorTask;
  isStopping: boolean;
  isStarting: boolean;
  loadingContent: boolean;
  onStop: (monitorId: string, e: React.MouseEvent) => void;
  onStart: (monitorId: string, symbol: string, bar: BarInterval, e: React.MouseEvent) => void;
  onViewPrompt: (
    monitor: { monitorId: string; symbol: string; bar: BarInterval; promptHash: string },
    e: React.MouseEvent,
  ) => void;
  onViewReport: (hash: string, e: React.MouseEvent) => void;
  onClick: (monitor: MonitorTask) => void;
}

const MonitorCard: React.FC<MonitorCardProps> = ({
  monitor,
  isStopping,
  isStarting,
  loadingContent,
  onStop,
  onStart,
  onViewPrompt,
  onViewReport,
  onClick,
}) => {
  const signalStatus = getSignalStatus(monitor.signals);

  return (
    <div
      className={`${
        monitor.status === 'running' ? 'bg-white/5 border-white/20' : 'bg-white/3 border-white/10 opacity-70'
      } border rounded-xl p-4 text-white/90 mb-2 cursor-pointer hover:bg-white/10 transition-colors`}
      onClick={() => onClick(monitor)}
    >
      {/* 第一行：币种和时间粒度 */}
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <h3 className="font-bold text-white text-lg truncate">{monitor.symbol.toUpperCase()}</h3>
          <span className="px-2 py-1 bg-white/10 border border-white/20 rounded text-xs text-white/70 flex-shrink-0">
            {getTimeFrameName(monitor.bar)}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {monitor.enableAutoTrading && monitor.status === 'running' && (
            <span className="px-2 py-1 rounded text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/50">
              自动交易
            </span>
          )}
          <span
            className={`px-2 py-1 rounded text-xs font-semibold ${
              monitor.status === 'running'
                ? 'bg-green-500/20 text-green-300 border border-green-500/50'
                : 'bg-gray-500/20 text-gray-300 border border-gray-500/50'
            }`}
          >
            {monitor.status === 'running' ? '运行中' : '已停止'}
          </span>
        </div>
        {monitor.regenerateStatus === 'failed' && monitor.regenerateFailureReason && (
          <div className="mt-1 text-xs text-red-300 bg-red-500/10 p-2 rounded border border-red-500/30 break-all">
            失败原因: {monitor.regenerateFailureReason}
          </div>
        )}
      </div>

      {/* 第二行：当前价格和指标 */}
      {monitor.currentData && (
        <div className="mb-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-white/60 text-sm">当前价格:</span>
            <span className="text-white font-semibold">{monitor.currentData.price}</span>
          </div>
          {monitor.currentData.rsi14 !== undefined && (
            <div className="flex items-center justify-between">
              <span className="text-white/60 text-sm">RSI(14):</span>
              <span className="text-white">{monitor.currentData.rsi14}</span>
            </div>
          )}
          {monitor.currentData.macd !== undefined && (
            <div className="flex items-center justify-between">
              <span className="text-white/60 text-sm">MACD:</span>
              <span className="text-white">
                {typeof monitor.currentData.macd === 'object'
                  ? monitor.currentData.macd?.macd
                  : monitor.currentData.macd}
              </span>
            </div>
          )}
        </div>
      )}

      {/* 第三行：时间范围和重新生成状态 */}
      <div className="mb-3 space-y-2">
        {monitor.ruleStartTime && monitor.ruleEndTime && (
          <div className="flex items-center justify-between">
            <span className="text-white/60 text-sm">时间范围:</span>
            <span className="text-white text-xs">
              {new Date(monitor.ruleStartTime * 1000).toLocaleString('zh-CN', {
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}{' '}
              -{' '}
              {new Date(monitor.ruleEndTime * 1000).toLocaleString('zh-CN', {
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
        )}
        {monitor.systemPromptId && (
          <div className="flex items-center justify-between">
            <span className="text-white/60 text-sm">提示词ID:</span>
            <span className="text-white text-xs font-mono">{monitor.systemPromptId}</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-white/60 text-sm">重新生成状态:</span>
          <span
            className={`text-xs px-2 py-1 rounded ${
              monitor.regenerateStatus === 'idle'
                ? 'bg-gray-500/20 text-gray-300'
                : monitor.regenerateStatus === 'generating'
                  ? 'bg-yellow-500/20 text-yellow-300'
                  : monitor.regenerateStatus === 'analyzing'
                    ? 'bg-blue-500/20 text-blue-300'
                    : monitor.regenerateStatus === 'completed'
                      ? 'bg-green-500/20 text-green-300'
                      : monitor.regenerateStatus === 'failed'
                        ? 'bg-red-500/20 text-red-300'
                        : 'bg-gray-500/20 text-gray-300'
            }`}
          >
            {monitor.regenerateStatus === 'idle'
              ? '空闲'
              : monitor.regenerateStatus === 'generating'
                ? '生成中'
                : monitor.regenerateStatus === 'analyzing'
                  ? '分析中'
                  : monitor.regenerateStatus === 'completed'
                    ? '已完成'
                    : monitor.regenerateStatus === 'failed'
                      ? '失败'
                      : '未知'}
          </span>
        </div>
      </div>

      {/* 第四行：信号状态 */}
      <div className="mb-3">
        <div className="flex items-center justify-between">
          <span className="text-white/60 text-sm">监控信号:</span>
          <span className="text-white">
            {signalStatus.triggeredCount} / {signalStatus.totalCount} 已触发
          </span>
        </div>
        {signalStatus.triggeredCount > 0 && (
          <div className="mt-2 space-y-1">
            {monitor.signals
              .filter((s) => s.triggered)
              .map((signal, idx) => (
                <div
                  key={idx}
                  className={`px-2 py-1 rounded text-xs ${
                    signal.type === ORDER_SIDE.BUY
                      ? 'bg-green-500/20 text-green-300 border border-green-500/50'
                      : 'bg-red-500/20 text-red-300 border border-red-500/50'
                  }`}
                >
                  {signal.type === ORDER_SIDE.BUY ? '🟢' : '🔴'} {signal.description}
                </div>
              ))}
          </div>
        )}
      </div>

      {/* AI 评分 */}
      {monitor.analysisScore !== undefined && (
        <div className="mb-3">
          <div className="flex items-center justify-between">
            <span className="text-white/60 text-sm">AI 评分:</span>
            <span
              className={`text-sm font-bold ${
                monitor.analysisScore >= 90
                  ? 'text-green-400'
                  : monitor.analysisScore >= 70
                    ? 'text-blue-400'
                    : 'text-gray-400'
              }`}
            >
              {monitor.analysisScore} 分
              {monitor.analysisDescription && (
                <span className="ml-1 text-xs font-normal text-white/50">({monitor.analysisDescription})</span>
              )}
            </span>
          </div>
        </div>
      )}

      {/* 第五行：更新时间 */}
      <div className="mt-3">
        <div className="text-white/40 text-xs">
          更新时间: {monitor.updatedAt ? new Date(monitor.updatedAt).toLocaleString('zh-CN') : '未知'}
        </div>
      </div>

      {/* 第六行：操作按钮 */}
      <div className="flex items-center gap-2 mt-2">
        {monitor.status === 'running' ? (
          <button
            onClick={(e) => onStop(monitor.monitorId, e)}
            disabled={isStopping}
            className={`flex-1 px-3 py-1.5 bg-red-500/20 text-red-300 border border-red-500/50 rounded-lg hover:bg-red-500/30 transition-colors text-xs flex items-center justify-center gap-2 ${
              isStopping ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isStopping ? (
              <>
                <svg
                  className="animate-spin h-3 w-3"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>停止中</span>
              </>
            ) : (
              '停止'
            )}
          </button>
        ) : (
          <button
            onClick={(e) => onStart(monitor.monitorId, monitor.symbol, monitor.bar, e)}
            disabled={isStarting}
            className={`flex-1 px-3 py-1.5 bg-green-500/20 text-green-300 border border-green-500/50 rounded-lg hover:bg-green-500/30 transition-colors text-xs flex items-center justify-center gap-2 ${
              isStarting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isStarting ? (
              <>
                <svg
                  className="animate-spin h-3 w-3"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>启动中</span>
              </>
            ) : (
              '启动'
            )}
          </button>
        )}
        <button
          onClick={(e) =>
            monitor.promptHash &&
            onViewPrompt(
              {
                monitorId: monitor.monitorId,
                symbol: monitor.symbol,
                bar: monitor.bar as BarInterval,
                promptHash: monitor.promptHash,
              },
              e,
            )
          }
          disabled={!monitor.promptHash || loadingContent}
          className={`flex-1 px-3 py-1.5 border rounded-lg transition-colors text-xs flex items-center justify-center gap-1 ${
            monitor.promptHash
              ? 'bg-blue-500/20 text-blue-300 border-blue-500/50 hover:bg-blue-500/30'
              : 'bg-white/5 text-white/30 border-white/10 cursor-not-allowed'
          } disabled:opacity-50`}
          title={monitor.promptHash ? '查看提示词' : '该监控任务没有提示词'}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          提示词
        </button>
        <button
          onClick={(e) => monitor.analysisReportHash && onViewReport(monitor.analysisReportHash, e)}
          disabled={!monitor.analysisReportHash || loadingContent}
          className={`flex-1 px-3 py-1.5 border rounded-lg transition-colors text-xs flex items-center justify-center gap-1 ${
            monitor.analysisReportHash
              ? 'bg-purple-500/20 text-purple-300 border-purple-500/50 hover:bg-purple-500/30'
              : 'bg-white/5 text-white/30 border-white/10 cursor-not-allowed'
          } disabled:opacity-50`}
          title={monitor.analysisReportHash ? '查看分析报告' : '该监控任务没有分析报告'}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          分析报告
        </button>
      </div>
    </div>
  );
};

export default MonitorCard;
